import { getPlatformInfo } from '../utils/platformUtils';
import { NotificationService } from './notification/NotificationService';

/**
 * 权限状态类型
 */
export type PermissionStatusType = 'granted' | 'denied' | 'prompt' | 'unsupported' | 'checking' | 'error';

/**
 * 权限信息接口
 */
export interface PermissionInfo {
  name: string;
  key: string;
  status: PermissionStatusType;
  supported: boolean;
  description: string;
  lastChecked?: Date;
  error?: string;
}

/**
 * 权限检查服务
 * 统一管理各种客户端权限的检查
 */
export class PermissionChecker {

  /**
   * 检查麦克风权限
   */
  static async checkMicrophonePermission(): Promise<PermissionInfo> {
    try {
      // 检查是否支持 getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return {
          name: '麦克风权限',
          key: 'microphone',
          status: 'unsupported',
          supported: false,
          description: '当前环境不支持麦克风访问',
          lastChecked: new Date()
        };
      }

      // 尝试检查权限状态
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          return {
            name: '麦克风权限',
            key: 'microphone',
            status: result.state as PermissionStatusType,
            supported: true,
            description: '用于语音输入和录音功能',
            lastChecked: new Date()
          };
        } catch (permissionError) {
          // 某些浏览器不支持 permissions.query for microphone
          console.debug('[PermissionChecker] Permissions API not available for microphone');
        }
      }

      // 如果无法通过 Permissions API 检查，返回支持但状态未知
      return {
        name: '麦克风权限',
        key: 'microphone',
        status: 'prompt',
        supported: true,
        description: '用于语音输入和录音功能（需要用户触发时请求）',
        lastChecked: new Date()
      };

    } catch (error) {
      return {
        name: '麦克风权限',
        key: 'microphone',
        status: 'error',
        supported: true,
        description: '检查权限时发生错误',
        lastChecked: new Date(),
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * 检查通知权限
   */
  static async checkNotificationPermission(): Promise<PermissionInfo> {
    try {
      const notificationService = NotificationService.getInstance();
      const status = await notificationService.checkPermission();

      return {
        name: '通知权限',
        key: 'notification',
        status: status as PermissionStatusType,
        supported: true,
        description: '用于任务提醒和系统通知',
        lastChecked: new Date()
      };
    } catch (error) {
      return {
        name: '通知权限',
        key: 'notification',
        status: 'error',
        supported: true,
        description: '检查通知权限时发生错误',
        lastChecked: new Date(),
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * 检查相机权限
   */
  static async checkCameraPermission(): Promise<PermissionInfo> {
    try {
      // 检查是否支持 getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return {
          name: '相机权限',
          key: 'camera',
          status: 'unsupported',
          supported: false,
          description: '当前环境不支持相机访问',
          lastChecked: new Date()
        };
      }

      // 尝试检查权限状态
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
          return {
            name: '相机权限',
            key: 'camera',
            status: result.state as PermissionStatusType,
            supported: true,
            description: '用于拍照和头像上传功能',
            lastChecked: new Date()
          };
        } catch (permissionError) {
          console.debug('[PermissionChecker] Permissions API not available for camera');
        }
      }

      // 如果无法通过 Permissions API 检查，返回支持但状态未知
      return {
        name: '相机权限',
        key: 'camera',
        status: 'prompt',
        supported: true,
        description: '用于拍照和头像上传功能（需要用户触发时请求）',
        lastChecked: new Date()
      };

    } catch (error) {
      return {
        name: '相机权限',
        key: 'camera',
        status: 'error',
        supported: true,
        description: '检查权限时发生错误',
        lastChecked: new Date(),
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * 检查位置权限
   */
  static async checkLocationPermission(): Promise<PermissionInfo> {
    try {
      // 检查是否支持地理位置
      if (!navigator.geolocation) {
        return {
          name: '位置权限',
          key: 'location',
          status: 'unsupported',
          supported: false,
          description: '当前环境不支持地理位置访问',
          lastChecked: new Date()
        };
      }

      // 尝试检查权限状态
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
          return {
            name: '位置权限',
            key: 'location',
            status: result.state as PermissionStatusType,
            supported: true,
            description: '用于基于位置的任务提醒',
            lastChecked: new Date()
          };
        } catch (permissionError) {
          console.debug('[PermissionChecker] Permissions API not available for geolocation');
        }
      }

      // 如果无法通过 Permissions API 检查，返回支持但状态未知
      return {
        name: '位置权限',
        key: 'location',
        status: 'prompt',
        supported: true,
        description: '用于基于位置的任务提醒（需要用户触发时请求）',
        lastChecked: new Date()
      };

    } catch (error) {
      return {
        name: '位置权限',
        key: 'location',
        status: 'error',
        supported: true,
        description: '检查权限时发生错误',
        lastChecked: new Date(),
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * 检查推送通知权限（仅原生平台）
   */
  static async checkPushNotificationPermission(): Promise<PermissionInfo> {
    const platformInfo = getPlatformInfo();

    // 只在原生平台检查推送通知
    if (!platformInfo.isNative) {
      return {
        name: '推送通知',
        key: 'pushNotification',
        status: 'unsupported',
        supported: false,
        description: '仅在原生应用中支持',
        lastChecked: new Date()
      };
    }

    try {
      // 动态导入 Capacitor 插件
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const result = await PushNotifications.checkPermissions();

      return {
        name: '推送通知',
        key: 'pushNotification',
        status: result.receive as PermissionStatusType,
        supported: true,
        description: '用于远程推送通知',
        lastChecked: new Date()
      };
    } catch (error) {
      return {
        name: '推送通知',
        key: 'pushNotification',
        status: 'error',
        supported: true,
        description: '检查推送通知权限时发生错误',
        lastChecked: new Date(),
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * 检查本地通知权限（仅原生平台）
   */
  static async checkLocalNotificationPermission(): Promise<PermissionInfo> {
    const platformInfo = getPlatformInfo();

    // 只在原生平台检查本地通知
    if (!platformInfo.isNative) {
      return {
        name: '本地通知',
        key: 'localNotification',
        status: 'unsupported',
        supported: false,
        description: '仅在原生应用中支持',
        lastChecked: new Date()
      };
    }

    try {
      // 动态导入 Capacitor 插件
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      const result = await LocalNotifications.checkPermissions();

      return {
        name: '本地通知',
        key: 'localNotification',
        status: result.display as PermissionStatusType,
        supported: true,
        description: '用于本地定时通知',
        lastChecked: new Date()
      };
    } catch (error) {
      return {
        name: '本地通知',
        key: 'localNotification',
        status: 'error',
        supported: true,
        description: '检查本地通知权限时发生错误',
        lastChecked: new Date(),
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * 获取所有权限状态
   */
  static async getAllPermissions(): Promise<PermissionInfo[]> {
    const platformInfo = getPlatformInfo();

    // 基础权限（所有平台都检查）
    const basePermissions = [
      this.checkMicrophonePermission(),
      this.checkNotificationPermission(),
      this.checkCameraPermission(),
      this.checkLocationPermission()
    ];

    // 原生平台额外权限
    if (platformInfo.isNative) {
      basePermissions.push(
        this.checkPushNotificationPermission(),
        this.checkLocalNotificationPermission()
      );
    }

    try {
      const results = await Promise.all(basePermissions);
      return results;
    } catch (error) {
      console.error('[PermissionChecker] Error getting all permissions:', error);
      return [];
    }
  }

  /**
   * 获取权限状态的显示文本
   */
  static getPermissionStatusText(status: PermissionStatusType): string {
    switch (status) {
      case 'granted': return '已授权';
      case 'denied': return '已拒绝';
      case 'prompt': return '待询问';
      case 'unsupported': return '不支持';
      case 'checking': return '检查中';
      case 'error': return '错误';
      default: return '未知';
    }
  }

  /**
   * 获取权限状态的图标
   */
  static getPermissionStatusIcon(status: PermissionStatusType): string {
    switch (status) {
      case 'granted': return '✅';
      case 'denied': return '❌';
      case 'prompt': return '❓';
      case 'unsupported': return '➖';
      case 'checking': return '🔄';
      case 'error': return '⚠️';
      default: return '❓';
    }
  }
}
